import { ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useAutoAddStore } from './autoAdd'
import { useCommonStore } from './common'
import { BrandID, CourseObject } from '@/types/common/course'
import { sendGtag } from '@/utils/gtag'
import { getInitialData } from '@/utils/api/top'
import { removeLoadingSkeleton } from '@/utils/dom'
import { GetPlatformInformationRes } from '@/types/jssdk'
import { getPlatformInformation } from '@/utils/pp'
import { NotificationItem } from '@/services/wbff/top/type'
import { CourseWithProfitLoss } from '@/services/wbff/asset/types'
import { CryptoCourses } from '@/const/courses'
import { loggerFatal } from '@/utils/user'

export type CarouselBanner = {
  IMG_URL: string
  LINK_URL: string
  BROWSER_FLAG: string
}

export const useTopStore = defineStore('top', () => {
  const commonStore = useCommonStore()
  const { setHasUnreadNotices, setGlobalConfig, setUserGroup, setTabBarIcon, setOprStatusGA } =
    commonStore
  const { globalConfig, userGroup } = storeToRefs(commonStore)

  const autoAddStore = useAutoAddStore()
  const { setAutoAddBrandID, setNextBrandID } = autoAddStore

  const topBanners = ref<Array<CarouselBanner>>([])
  const otherBanner = ref<CarouselBanner | null>(null)
  const attentionFlag = ref<number | null>(null)
  const possessBrandFlag = ref<number | null>(null)
  const campaignAppliedFlag = ref<number>(0)
  const miniAppAssetManagementOpenFlag = ref<boolean | null>(null)

  const tmpBrands = ref<Array<CourseObject>>([])
  const tmpBrandsObj = ref<any>(null)
  const isInvestMiniAppAccountOpened = ref<boolean>(false)
  const defaultGraphFlag = ref<boolean>(false)

  const userAssetsReturns = ref(null)
  const tmpAssets = ref(null)

  const paypayPointAmount = ref<number>(0)
  const courseList = ref<Array<CourseWithProfitLoss>>([])

  const assets = ref(null)

  const notificationItems = ref<Array<NotificationItem>>([])

  const modalOptions = ref({
    aboutAvailableAssets: {
      isVisible: false,
    },
    specificCourse: {
      isVisible: false,
      brandId: BrandID.NONE,
      amount: 0,
    },
    generalCourse: {
      isVisible: false,
      triggeredSource: '',
    },
    noAccount: {
      isVisible: false,
      triggeredSource: '',
      ekycStatus: '',
    },
    noPointsHasAccount: {
      isVisible: false,
    },
  })

  const clientId = ref<string | null>(null)
  const platformInformation = ref<GetPlatformInformationRes | null>(null)
  const osType = ref<'ios' | 'android' | null>(null)
  const osVersion = ref<number | null>(null)
  const availablePoint = ref(0)
  const isTopAssetsHidden = ref<boolean>(localStorage.getItem('isTopAssetsHidden') === 'true')

  const toggleIsTopAssetsHidden = () => {
    isTopAssetsHidden.value = !isTopAssetsHidden.value
    localStorage.setItem('isTopAssetsHidden', isTopAssetsHidden.value.toString())
  }

  const setAttentionFlag = (val: number) => {
    attentionFlag.value = val
  }

  const setPossessBrandFlag = (val: number | null) => {
    possessBrandFlag.value = val
  }

  const setCampaignAppliedFlag = (val: number) => {
    campaignAppliedFlag.value = val
  }

  const setMiniAppAssetManagementOpenFlag = (val: boolean) => {
    miniAppAssetManagementOpenFlag.value = val
  }

  const setIsInvestMiniAppAccountOpened = (val: boolean) => {
    isInvestMiniAppAccountOpened.value = val
  }

  const setDefaultGraphFlag = (val: boolean) => {
    defaultGraphFlag.value = val
  }

  const setTmpAssets = (val) => {
    tmpAssets.value = val
  }

  const setTmpBrands = (val) => {
    tmpBrands.value = val
  }

  const setTmpBrandObj = (val) => {
    tmpBrandsObj.value = val
  }

  const setUserAssetsReturns = (val) => {
    userAssetsReturns.value = val
  }

  const setAssets = (val) => {
    assets.value = val
  }

  const setModalOptionsByModal = (key: string, val) => {
    const newModalOptions = Object.assign({}, modalOptions.value)
    newModalOptions[key] = val
    modalOptions.value = newModalOptions
  }

  const setModalOptionsVisible = (key: string, isVisible: boolean) => {
    const newModalOptions = Object.assign({}, modalOptions.value)
    newModalOptions[key].isVisible = isVisible
    modalOptions.value = newModalOptions
  }

  const setPaypayPointAmount = (val: number) => {
    paypayPointAmount.value = val
  }

  const setCourseList = (val) => {
    courseList.value = val
  }

  const setNotificationItems = (val) => {
    notificationItems.value = val
  }

  const setClientId = (val) => {
    clientId.value = val
  }

  const newInitProject = async (): Promise<void> => {
    const response = await getInitialData()

    if (!response.isSuccess) {
      removeLoadingSkeleton()
      return
    }

    const { payload } = response

    sendGtag({
      event: 'event',
      eventact: 'config',
      eventcate: 'userid',
      eventlab: payload.clientId,
    })
    setAttentionFlag(payload.attentionFlag)
    setPossessBrandFlag(payload.possessBrandFlag)
    const oprStatusGA = payload.possessBrandFlag !== 1 ? '未運用' : '運用中'
    setOprStatusGA(oprStatusGA)
    if (payload.campaignAppliedFlag !== null) {
      setCampaignAppliedFlag(payload.campaignAppliedFlag)
    }
    if (payload.miniAppAssetManagementOpenFlag !== null) {
      setMiniAppAssetManagementOpenFlag(payload.miniAppAssetManagementOpenFlag === 1)
    }

    g.CSRF_TOKEN = payload.csrfToken
    setClientId(payload.clientId)
    setUserGroup(payload.userGroup)
    setIsInvestMiniAppAccountOpened(payload.accountType === 1)

    setDefaultGraphFlag(payload.defaultGraphFlag === 1)
    if (payload.abFlag === 'A') {
      sendGtag({
        event: 'event',
        eventact: 'intermediary',
        eventcate: 'pattern_A',
      })
      if (payload.possessBrandFlag !== 1) {
        sendGtag({
          event: 'event',
          eventact: 'Not_Ope_Top',
          eventcate: 'Not_Ope',
          eventlab: 'View_TypeA',
        })
      }
    } else if (payload.abFlag === 'B') {
      sendGtag({
        event: 'event',
        eventact: 'intermediary',
        eventcate: 'pattern_B',
      })
      if (payload.possessBrandFlag !== 1) {
        sendGtag({
          event: 'event',
          eventact: 'Not_Ope_Top',
          eventcate: 'Not_Ope',
          eventlab: 'View_TypeB',
        })
      }
    }

    if (localStorage.walletFirst * 1 !== 1) {
      localStorage.walletFirst = payload.providedToThirdPartiesFlag * 1
    }

    // TODO: temporarily keep this for dependencies in wbff service
    g.tmpBrands = payload.seBrandArray
    setTmpBrands(payload.seBrandArray)
    setTmpBrandObj(ES.arrayToJson(payload.seBrandArray, 'BRAND_ID'))

    const profitLossInfoBrandList = payload.userAssetAll.PROFIT_LOSS_INFO_BRAND_LIST || []
    setTmpAssets(ES.arrayToJson(profitLossInfoBrandList, 'BRAND_ID'))
    setAssets(payload.userAssetAll)
    // For legacy logics that are not yet refactored
    g._sysDate = ES.date(payload.userAssetAll.SYSTEM_DATE)

    setCourseList(payload.courseListWithProfitLoss)
    setHasUnreadNotices(payload.hasUnread)

    if (payload.autosweepAllCourseFlag * 1) {
      setNextBrandID(BrandID.ALL)
      setAutoAddBrandID(BrandID.ALL)
    } else {
      setNextBrandID(payload.autosweepCourseId)
      setAutoAddBrandID(payload.autosweepCourseId)
    }

    setGlobalConfig(payload.globalConfig)
    setUserAssetsReturns(payload.returns)
    setNotificationItems(payload.topNoticeArray)
    setTabBarIcon()
    topBanners.value = payload.topBanners
    otherBanner.value = payload.otherBanner
    platformInformation.value = await getPlatformInformation()
    availablePoint.value = payload.availablePoint

    removeLoadingSkeleton()
  }

  const onClickAssetManagementLink = (triggeredSource: string) => {
    if (isInvestMiniAppAccountOpened.value) {
      const isOnlyCryptoCourse = Object.keys(tmpAssets.value || {})
        .filter((key) => tmpAssets.value[key].PROFIT_LOSS_INFO.VALUATION_AMOUNT > 0)
        .every((key) => {
          const { BRAND_ID } = tmpAssets.value[key]
          return CryptoCourses.includes(BRAND_ID)
        })
      if (possessBrandFlag.value == 1 && !isOnlyCryptoCourse) {
        ;(modalOptions.value.generalCourse.triggeredSource as unknown as string) = triggeredSource
        modalOptions.value.generalCourse.isVisible = true
      } else {
        modalOptions.value.noPointsHasAccount.isVisible = true
      }
    } else {
      ;(modalOptions.value.noAccount.triggeredSource as unknown as string) = triggeredSource
      modalOptions.value.noAccount.isVisible = true
    }
  }

  return {
    attentionFlag,
    possessBrandFlag,
    assets,
    campaignAppliedFlag,
    isInvestMiniAppAccountOpened,
    defaultGraphFlag,
    modalOptions,
    paypayPointAmount,
    globalConfig,
    userGroup,
    miniAppAssetManagementOpenFlag,
    userAssetsReturns,
    tmpAssets,
    tmpBrands,
    tmpBrandsObj,
    courseList,
    notificationItems,
    clientId,
    setPossessBrandFlag,
    setAttentionFlag,
    setModalOptionsByModal,
    setModalOptionsVisible,
    setPaypayPointAmount,
    setTmpAssets,
    setCourseList,
    newInitProject,
    onClickAssetManagementLink,
    osType,
    osVersion,
    platformInformation,
    topBanners,
    otherBanner,
    availablePoint,
    isTopAssetsHidden,
    toggleIsTopAssetsHidden,
  }
})
